/**
 * Geração da Seção D - Apreciações de riscos
 * Lista os riscos agrupados por parte da máquina com todos os detalhes
 */

import { Risco } from '../core/latex-types';
import { escapeLatex } from '../core/latex-utils';
import {
  gerarTabelaTecnicaApreciacaoRiscos,
} from '../tables/latex-tabela-tecnica';

/**
 * Gera o conteúdo completo de uma parte individual na Seção D usando tabela técnica consolidada
 */
function gerarConteudoParte(_parte: string, riscos: Risco[], numeroParte: number, referencia: string = ''): string {
  if (!riscos || riscos.length === 0) {
    return '';
  }

  // Usar o primeiro risco para gerar a tabela técnica
  // Se houver múltiplos riscos na mesma parte, cada um pode ter sua própria tabela
  // Por enquanto, geramos uma tabela por parte usando o primeiro risco
  const primeiroRisco = riscos[0];
  if (!primeiroRisco) {
    return '';
  }

  // Gerar tabela técnica completa com numeração e referência (No Emitente ou SISMANA)
  return gerarTabelaTecnicaApreciacaoRiscos(primeiroRisco, numeroParte, referencia);
}

/**
 * Gera a Seção D completa - Apreciações de riscos
 * @param riscos Array de riscos
 * @param referencia Referência para numeração (No Emitente ou SISMANA)
 */
export function gerarSecaoDApreciacoesRiscos(riscos: Risco[], referencia: string = ''): string {
  if (!riscos || riscos.length === 0) {
    return '';
  }

  // Agrupar riscos por parte
  const partesMap = new Map<string, Risco[]>();
  const riscosOrdenados = [...riscos].sort((a, b) => (a.numero || 0) - (b.numero || 0));

  // Coletar todas as partes únicas primeiro
  const partesUnicas = new Set<string>();
  riscosOrdenados.forEach((risco) => {
    const parte = risco.parte || 'Sem parte definida';
    partesUnicas.add(parte);
  });

  // Criar mapeamento de parte para riscos
  riscosOrdenados.forEach((risco) => {
    const parte = risco.parte || 'Sem parte definida';
    if (!partesMap.has(parte)) {
      partesMap.set(parte, []);
    }
    partesMap.get(parte)!.push(risco);
  });

  // Ordenar partes por ordem de aparição
  const partesArray = Array.from(partesUnicas);
  const partesOrdenadas = Array.from(partesMap.entries()).sort((a, b) => {
    const indiceA = partesArray.indexOf(a[0]);
    const indiceB = partesArray.indexOf(b[0]);
    return indiceA - indiceB;
  });

  let latex = `
\\newpage

\\noindent\\section*{Seção D - Apreciações de riscos}
\\addcontentsline{toc}{section}{D. Apreciações de riscos}
\\label{sec:apreciacoes-riscos}

`;

  // Gerar subseção para cada parte com todos os detalhes
  partesOrdenadas.forEach(([parte, riscosParte], index) => {
    const numeroSubsecao = index + 1;
    const nomeParte = escapeLatex(parte);

    // Adicionar quebra de página antes de D.2, D.3, etc. (não antes de D.1)
    if (numeroSubsecao > 1) {
      latex += `\\newpage
`;
    }

    latex += `\\subsection*{D.${numeroSubsecao}. ${nomeParte}}
\\addcontentsline{toc}{subsection}{D.${numeroSubsecao}. ${nomeParte}}
\\label{sec:apreciacao-riscos-${numeroSubsecao}}

`;

    // Gerar conteúdo completo da parte com referência
    latex += gerarConteudoParte(parte, riscosParte, numeroSubsecao, referencia);

    latex += `
`;
  });

  return latex;
}

